import React, { useState } from 'react';
import { Controller } from 'react-hook-form';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  Form,
  FormGroup,
  Modal,
  Row,
  Spinner,
} from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import usePropertySalesPrices from '../../hooks/usePropertySalesPrices';
import TakeDownPropertyModal from '../TakeDownPropertyModal';
import useModalState from '../../../../hooks/useModalState';
import { PropertySalesProps } from '../../models/propertyModel';
import {
  getPropertySaleStatus,
  getStatusBadgeColor,
} from '../../adapters/propertyAdapter';
import ActivatePropertyModal from '../ActivatePropertyModal';
import { PROPERTY_SALE_STATUS_VALUES } from '../../constants/propertyConstants';
import MoneyInput from '../../../../components/MoneyInput';
import usePropertySalesInfo from '../../hooks/usePropertySalesInfo';
import SectionTitle from './SectionTitle';
import SalesAvailabilityFields from './SalesAvailabilityFields';
import { CURRENCY_SYMBOLS } from './salesFields';

type Props = PropertySalesProps & {
  currency?: string;
  // Formulario de info de venta compartido con la tarjeta "Información de propiedad en venta".
  salesInfo: ReturnType<typeof usePropertySalesInfo>;
};

export const PropertySalesPrices = (props: Props) => {
  const navigate = useNavigate();
  const [takeDownPropertyModal, toggleTakeDownPropertyModal] = useModalState();
  const [activatePropertyModal, toggleActivatePropertyModal] = useModalState();
  const [soldModal, toggleSoldModal] = useModalState();
  const {
    // form
    handleSubmit,
    control,
    errors,
    // loading
    isLoading: isSavingPrices,
    updateMarketplaceStatus,
    updateMarketplacePrices,
    markPropertyAsSold,
  } = usePropertySalesPrices(props, props.salesInfo);

  const { salesInfo } = props;
  // El botón y los modales se bloquean mientras se guarda cualquiera de los dos formularios.
  const isLoading = isSavingPrices || salesInfo.isLoading;
  const symbol = CURRENCY_SYMBOLS[props.currency ?? 'USD'] ?? props.currency;
  const isPublished =
    props.property_sale_state === PROPERTY_SALE_STATUS_VALUES.PUBLISHED;
  const isOnTransfer =
    props.property_sale_state ===
    PROPERTY_SALE_STATUS_VALUES.ON_TRANSFER_OWNERSHIP;

  return (
    <Card>
      <CardHeader className="d-flex align-items-center justify-content-between py-3">
        <h3 className="mb-0">Configurar venta</h3>
        <Badge color={getStatusBadgeColor(props.property_sale_state)}>
          {getPropertySaleStatus(props.property_sale_state)}
        </Badge>
      </CardHeader>

      <Form role="form" onSubmit={handleSubmit(updateMarketplacePrices)}>
        <CardBody className="py-3">
          <SectionTitle title="Precios" />
          <Row>
            <Col lg="3" md="6">
              <FormGroup className="mb-3">
                <label className="form-control-label" htmlFor="sale_price">
                  Precio de venta
                </label>
                <Controller
                  name="sale_price"
                  control={control}
                  render={({ field }) => (
                    <MoneyInput
                      id="sale_price"
                      symbol={symbol}
                      value={field.value}
                      onChange={field.onChange}
                      invalid={!!errors.sale_price?.message}
                    />
                  )}
                />
                {errors.sale_price?.message && (
                  <h5 className="text-danger mt-1 mb-0">
                    {errors.sale_price.message}
                  </h5>
                )}
              </FormGroup>
            </Col>
            <Col lg="3" md="6">
              <FormGroup className="mb-3">
                <label className="form-control-label" htmlFor="sale_min_price">
                  Precio mínimo de venta
                </label>
                <Controller
                  name="sale_min_price"
                  control={control}
                  render={({ field }) => (
                    <MoneyInput
                      id="sale_min_price"
                      symbol={symbol}
                      value={field.value}
                      onChange={field.onChange}
                      invalid={!!errors.sale_min_price?.message}
                    />
                  )}
                />
                {errors.sale_min_price?.message ? (
                  <h5 className="text-danger mt-1 mb-0">
                    {errors.sale_min_price.message}
                  </h5>
                ) : (
                  <small style={{ color: '#525f7f' }}>
                    Lo mínimo que aceptarías en una negociación.
                  </small>
                )}
              </FormGroup>
            </Col>
          </Row>

          <SalesAvailabilityFields
            availableAt={salesInfo.availableAt}
            setAvailableAt={salesInfo.setAvailableAt}
          />

          <SectionTitle
            className="mt-2"
            title="Visibilidad en marketplace"
            hint={
              isPublished
                ? 'La propiedad se está mostrando en el marketplace de ventas.'
                : 'La propiedad no se muestra en el marketplace de ventas.'
            }
          />
          {isPublished ? (
            <Button
              type="button"
              onClick={toggleTakeDownPropertyModal}
              color="danger"
              outline
            >
              <i className="fa-solid fa-eye-slash mr-2" />
              Ocultar en marketplace
            </Button>
          ) : (
            <Button
              type="button"
              onClick={toggleActivatePropertyModal}
              color="success"
              outline
            >
              <i className="fa-solid fa-eye mr-2" />
              Publicar en marketplace
            </Button>
          )}
          {isOnTransfer && (
            <Button
              type="button"
              onClick={toggleSoldModal}
              color="danger"
              outline
              className="ml-2"
            >
              <i className="fa-solid fa-handshake mr-2" />
              Marcar como vendida
            </Button>
          )}
        </CardBody>
        <CardFooter className="text-right py-3">
          <Button
            type="button"
            color="secondary"
            outline
            className="mr-2"
            onClick={() => navigate(`/properties/admin/${props.property_id}`)}
          >
            Cancelar
          </Button>
          <Button color="primary" disabled={isLoading}>
            {isLoading && <Spinner size="sm" className="mr-2" />}
            Guardar cambios
          </Button>
        </CardFooter>
      </Form>
      <Modal isOpen={soldModal} toggle={toggleSoldModal} centered>
        <div className="modal-body text-center">
          <h4 className="mb-2">¿Marcar la propiedad como vendida?</h4>
          <p className="text-sm mb-0" style={{ color: '#525f7f' }}>
            Esta acción cierra la venta de la propiedad.
          </p>
        </div>
        <div className="modal-footer justify-content-center">
          <Button color="secondary" outline onClick={toggleSoldModal}>
            Cancelar
          </Button>
          <Button
            color="danger"
            disabled={isLoading}
            onClick={() => markPropertyAsSold(props.property_id)}
          >
            Sí, marcar como vendida
          </Button>
        </div>
      </Modal>
      <TakeDownPropertyModal
        marketplace_type="sale"
        property_id={props.property_id}
        isOpen={takeDownPropertyModal}
        onToggle={toggleTakeDownPropertyModal}
        onConfirmation={updateMarketplaceStatus}
        warningText={
          '¿Estás seguro de quitar la propiedad del marketplace de ventas?'
        }
        confirmationText={'Si, quitar del marketplace ventas'}
        abortText={'No, continuar revisando...'}
      />
      <ActivatePropertyModal
        marketplace_type="sale"
        property_id={props.property_id}
        isOpen={activatePropertyModal}
        onToggle={toggleActivatePropertyModal}
        onConfirmation={updateMarketplaceStatus}
        warningText={
          '¿Estás seguro de activar la propiedad en marketplace de ventas?'
        }
        confirmationText={'Si, activar del marketplace ventas'}
        abortText={'No, continuar revisando...'}
      />
    </Card>
  );
};
